<template>
  <div>
    <div class="mb-15">
      <div v-if="disabled" class="disabled-card" />
      <a
        v-if="!disabled"
        :href="freeWarehousing"
        class="vz-network-direction-warehousing"
      >
        Бесплатный "Простой машины"
      </a>
      <div class="text-h1 mb-10">
        {{ titleCard }}
      </div>
      <network-autocomplete
        ref="networkAutocomplete"
        v-model="networkGuid"
        :class="{'border-light': !networkGuid}"
        label="Торговая сеть"
        :placeholder="networkAutocompletePlaceholder"
        :rules="[formRules.required]"
        :disabled="disabled"
        @change="onChangeNetwork"
      />
      <network-branch-autocomplete
        ref="networkBranchAutocomplete"
        v-model="branchGuid"
        :class="{'border-light': !branchGuid && !isCustomAddress}"
        label="Филиал"
        :network-guid="networkGuid"
        :placeholder="branchAutocompletePlaceholder"
        :custom="isCustomAddress"
        :disabled="disabled || !networkGuid"
        :icon-name="branchIcon"
        :rules="branchRules"
        class="mt-15"
        @change="onBranchChange"
      />
    </div>

    <div>
      <vz-destination-direction
        v-if="isCustomAddress"
        v-model:location-guid="orderStore.orderForm.directions.destination.locationGuid"
        v-model:address="orderStore.orderForm.directions.destination.address"
        :cost="destinationCost"
        :disabled="disabled"
        direction="address"
        use-map
        hide-switcher
        direction-to
        title-card=""
      />
      <div v-if="!isCustomAddress">
        <vz-destination-direction-calendar :disabled="!branchGuid || disabled">
          <div
            v-if="orderStore.networkingAddress?.timetableMessage"
            class="text-caption vz-border-card network-warehousing-card"
            :class="disabled ? 'color-inactive' : 'color-info2'"
          >
            <span v-html="orderStore.networkingAddress.timetableMessage" />
          </div>
        </vz-destination-direction-calendar>
      </div>
    </div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import {TradingNetwork, TradingNetworkAddress, ValidationRule} from '~/types/types';
import NetworkAutocomplete from '~/components/autocomplete/network-autocomplete.vue';
import {useOrderStore} from '~/stores/order';
import formRules from '~/common/vz-form-rules';
import NetworkBranchAutocomplete from '~/components/autocomplete/network-branch-autocomplete.vue';
import VzDestinationDirectionCalendar
  from '~/components/personal/order/direction/vz-destination-direction-calendar.vue';
import VzDestinationDirection from '~/components/personal/order/direction/vz-destination-direction.vue';
import {freeWarehousing} from '~/api/constant-urls';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  titleCard: {
    type: String,
    default: 'Доставка в сеть',
  },

  networkAutocompletePlaceholder: {
    type: String,
    default: 'Введите текст для поиска',
  },
  branchAutocompletePlaceholder: {
    type: String,
    default: 'Введите текст для поиска',
  },
})

const orderStore = useOrderStore()

const networkBranchAutocomplete = ref()
const networkAutocomplete = ref()

const networkGuid = computed({
  get() {
    return orderStore.orderForm.directions.destination.networkGuid || ''
  },
  set(value: string) {
    orderStore.orderForm.directions.destination.networkGuid = value
  }
})

const selectedNetwork = computed(() => {
  return networkAutocomplete.value?.selected
})

const branchGuid = computed({
  get() {
    return orderStore.orderForm.directions.destination.networkAddressGuid || ''
  },
  set(value: string) {
    setNetworkAddress(value)
  }
})

const isCustomAddress = computed(() => {
  return !!orderStore.orderForm.directions.destination.isCustom
})

const branchIcon = computed(() => {
  return isCustomAddress.value ? '' : 'home'
})

const branchRules = computed(():ValidationRule[] => {
  return isCustomAddress.value ? [] : [formRules.required]
})

const destinationCost = computed(() => {
  return orderStore.orderPrice?.address?.destination?.price?.total
    ? ` (${orderStore.orderPrice?.address?.destination?.price?.total} ${orderStore.orderPriceCurrency.currency})`
    : ''
})

function setNetworkAddress(value: string) {
  orderStore.orderForm.directions.destination.isCustom = !value
  orderStore.orderForm.directions.destination.networkAddressGuid = value
}

function setNetworkBranch(value?: TradingNetworkAddress) {
  const unloadingOperations = orderStore.orderForm.specialRequirements.destination.unloadingOperations

  if (value?.locationGuid) {
    orderStore.orderForm.directions.destination.locationGuid = value.locationGuid
  }
  if (value?.guid) {
    orderStore.orderForm.directions.destination.address = value.address
    setNetworkAddress(value.guid)
    // Если нужно предустановить ПРР, либо снять предустановленные
    if (value.needLoading?.floor || (orderStore.networkingAddress &&
      +unloadingOperations.floor === orderStore.networkingAddress.needLoading?.floor &&
      unloadingOperations.freightElevator === orderStore.networkingAddress.needLoading.hasLift)
    ) {
      unloadingOperations.active = !!value.needLoading?.floor
      unloadingOperations.floor = value.needLoading?.floor || 0
      unloadingOperations.freightElevator = value.needLoading?.hasLift || false
    }
  } else if (value?.note) { // Нет нужного филиала
    orderStore.orderForm.directions.destination.address = value.note || ''
    setNetworkAddress('')
  }
}

async function onChangeNetwork(_: string, obj: TradingNetwork) {
  await orderStore.setTradingNetwork(obj)
}

function onBranchChange(_: string, obj: TradingNetworkAddress) {
  setNetworkBranch(obj)
}

watch(() => networkBranchAutocomplete.value?.selected,
  (value) => {
    // if (value && !value.guid) {
    //   value.address = value.note || ''
    // }
    // setNetworkBranch(value)
    // orderStore.setDefaultDate()
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.vz-network-direction-warehousing {
  font-size: $fs-12;
  position: absolute;
  top: -24px;
  right: 0;
  text-decoration: underline;
}
</style>